import {
  BarChartOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { ActionType, ProTable } from "@ant-design/pro-components";
import {
  Button,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Upload,
} from "antd";
import type { UploadProps } from "antd";
import { AntPageContainer } from "../../shared/components/AntPageContainer";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import useAlert from "../../shared/hooks/useAlert";
import { useNavigate } from "react-router-dom";
import surveyApi, {
  SurveyListItem,
  MultilingualText,
  SurveyImportRequest,
} from "../../survey/api";
import { useAuthUser } from "../../auth/hooks/useAuthUser";
import { UserRole } from "../../users/enums";

// Helper function to check if superadmin cookie exists
const isSuperAdmin = (): boolean => {
  return document.cookie
    .split(";")
    .some((item) => item.trim().startsWith("superadmin="));
};

const getLocalizedTitle = (
  title: MultilingualText | undefined,
  fallback: string = "Untitled"
): string => {
  if (!title) return fallback;
  return (
    title.en ||
    title.fr ||
    title.ko ||
    title.vi ||
    title.zh ||
    title.th ||
    title.es ||
    title.ru ||
    title.hi ||
    fallback
  );
};

// User type for dropdown
interface DropdownUser {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
}

export const SurveyList = () => {
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const ref = useRef<ActionType>();
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<string[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string | undefined>(
    undefined
  );
  const [importLoading, setImportLoading] = useState(false);

  // Change ownership modal state
  const [changeOwnerModalOpen, setChangeOwnerModalOpen] = useState(false);
  const [changeOwnerSurveyId, setChangeOwnerSurveyId] = useState<number | null>(
    null
  );
  const [changeOwnerLoading, setChangeOwnerLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState<DropdownUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(
    undefined
  );

  // Get current user to check role
  const { user } = useAuthUser();
  const isAdmin = user?.role === UserRole.Admin;

  // Check for superadmin status
  const showSuperAdminFeatures = useMemo(() => isSuperAdmin(), []);

  // Fetch available channels on mount
  useEffect(() => {
    const loadChannels = async () => {
      try {
        const response = await surveyApi.getChannels();
        setChannels(response.data || []);
      } catch (error) {
        console.error("Error fetching channels:", error);
      }
    };
    loadChannels();
  }, []);

  // Handle survey export
  const handleExport = useCallback(
    async (surveyId: number, publicId: string) => {
      try {
        await surveyApi.exportSurvey(surveyId, publicId);
        setAlert("Survey exported successfully!", "success");
      } catch (error) {
        console.error("Error exporting survey:", error);
        setAlert("Error exporting survey", "error");
      }
    },
    [setAlert]
  );

  // Handle survey import
  const handleImport = useCallback(
    async (file: File) => {
      try {
        setImportLoading(true);
        const text = await file.text();
        const surveyData: SurveyImportRequest = JSON.parse(text);

        // Basic validation (welcomeMessage is optional)
        if (!surveyData.title || !surveyData.sections) {
          throw new Error("Invalid survey format. Missing required fields.");
        }

        await surveyApi.importSurvey(surveyData);
        setAlert("Survey imported successfully!", "success");
        ref.current?.reload();
      } catch (error) {
        console.error("Error importing survey:", error);
        if (error instanceof SyntaxError) {
          setAlert("Invalid JSON file", "error");
        } else if (error instanceof Error) {
          setAlert(error.message, "error");
        } else {
          setAlert("Error importing survey", "error");
        }
      } finally {
        setImportLoading(false);
      }
    },
    [setAlert]
  );

  // Upload props for import
  const uploadProps: UploadProps = {
    accept: ".json",
    showUploadList: false,
    beforeUpload: (file) => {
      handleImport(file);
      return false; // Prevent default upload behavior
    },
  };

  const fetchSurveys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await surveyApi.getAll(selectedChannel);
      const data = response.data || [];
      return {
        data,
        success: true,
        total: data.length,
      };
    } catch (error) {
      console.error("Error fetching surveys:", error);
      setAlert("Error fetching surveys", "error");
      return {
        data: [],
        success: false,
        total: 0,
      };
    } finally {
      setLoading(false);
    }
  }, [setAlert, selectedChannel]);

  // Reload table when channel filter changes
  useEffect(() => {
    ref.current?.reload();
  }, [selectedChannel]);

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        await surveyApi.delete(id);
        setAlert("Survey deleted successfully!", "success");
        ref.current?.reload();
      } catch (error) {
        console.error("Error deleting survey:", error);
        setAlert("Error deleting survey", "error");
      }
    },
    [setAlert]
  );

  // Open change ownership modal and fetch users
  const handleOpenChangeOwnerModal = useCallback(async (surveyId: number) => {
    setChangeOwnerSurveyId(surveyId);
    setChangeOwnerModalOpen(true);
    setSelectedUserId(undefined);
    setUsersLoading(true);

    try {
      const response = await surveyApi.getUsersForDropdown();
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setAlert("Error fetching users", "error");
    } finally {
      setUsersLoading(false);
    }
  }, [setAlert]);

  // Handle change ownership submission
  const handleChangeOwner = useCallback(async () => {
    if (!changeOwnerSurveyId || !selectedUserId) {
      setAlert("Please select a user", "error");
      return;
    }

    setChangeOwnerLoading(true);
    try {
      await surveyApi.changeOwner(changeOwnerSurveyId, selectedUserId);
      setAlert("Survey ownership changed successfully!", "success");
      setChangeOwnerModalOpen(false);
      setChangeOwnerSurveyId(null);
      setSelectedUserId(undefined);
      ref.current?.reload();
    } catch (error) {
      console.error("Error changing ownership:", error);
      setAlert("Error changing survey ownership", "error");
    } finally {
      setChangeOwnerLoading(false);
    }
  }, [changeOwnerSurveyId, selectedUserId, setAlert]);

  // Close modal handler
  const handleCloseChangeOwnerModal = useCallback(() => {
    setChangeOwnerModalOpen(false);
    setChangeOwnerSurveyId(null);
    setSelectedUserId(undefined);
    setUsers([]);
  }, []);

  return (
    <AntPageContainer>
      <ProTable<SurveyListItem>
        request={fetchSurveys}
        loading={loading}
        onRequestError={() => {
          setAlert("Oops! Something went wrong retrieving the data!", "error");
        }}
        actionRef={ref}
        toolBarRender={() =>
          [
            <div key="channel-filter" className="flex items-center gap-2">
              <FilterOutlined className="text-gray-400" />
              <Select
                placeholder="Filter by Channel"
                allowClear
                style={{ width: 200 }}
                value={selectedChannel}
                onChange={(value) => setSelectedChannel(value)}
                options={[...channels.map((ch) => ({ value: ch, label: ch }))]}
              />
            </div>,
            showSuperAdminFeatures && (
              <Upload key="import" {...uploadProps}>
                <Button icon={<UploadOutlined />} loading={importLoading}>
                  Import Survey
                </Button>
              </Upload>
            ),
            <Button
              key="refresh"
              icon={<ReloadOutlined />}
              onClick={() => ref.current?.reload()}
            >
              Refresh
            </Button>,
            <Button
              key="create"
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => navigate("./create")}
            >
              Create Survey
            </Button>,
          ].filter(Boolean)
        }
        rowKey="id"
        search={false}
        pagination={{
          pageSize: 30,
          showSizeChanger: false,
        }}
        scroll={{ x: "max-content" }}
        columns={[
          {
            key: "title",
            title: "Survey Title",
            dataIndex: "title",
            width: 250,
            render: (_, record) => (
              <span
                className="font-medium cursor-pointer hover:text-blue-500 transition-colors"
                onClick={() => navigate(`./responses/${record.id}`)}
              >
                {getLocalizedTitle(record.title)}
              </span>
            ),
          },
          {
            key: "publicId",
            title: "Public ID",
            dataIndex: "publicId",
            width: 140,
            render: (_, record) => (
              <Tooltip title="Click to copy survey link">
                <code
                  className="bg-gray-100 px-2 py-1 rounded cursor-pointer hover:bg-gray-200"
                  onClick={() => {
                    const url = `${window.location.origin}/s/${record.publicId}`;
                    navigator.clipboard.writeText(url);
                    setAlert("Survey link copied to clipboard!", "success");
                  }}
                >
                  {record.publicId}
                </code>
              </Tooltip>
            ),
          },
          {
            key: "channels",
            title: "Channels",
            dataIndex: "channels",
            width: 180,
            render: (_, record) =>
              record.channels && record.channels.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {record.channels.map((ch) => (
                    <Tag key={ch} color="cyan">
                      {ch}
                    </Tag>
                  ))}
                </div>
              ) : (
                <span className="text-gray-400">-</span>
              ),
          },
          {
            key: "isActive",
            title: "Status",
            dataIndex: "isActive",
            width: 100,
            render: (_, record) =>
              record.isActive ? (
                <Tag color="green">Active</Tag>
              ) : (
                <Tag color="red">Inactive</Tag>
              ),
          },
          {
            key: "questionCount",
            title: "Questions",
            dataIndex: "questionCount",
            width: 100,
            align: "center",
            render: (_, record) => (
              <Tag color="blue">{record.questionCount}</Tag>
            ),
          },
          {
            key: "responseCount",
            title: "Responses",
            dataIndex: "responseCount",
            width: 100,
            align: "center",
            render: (_, record) => (
              <Tag color="purple">{record.responseCount}</Tag>
            ),
          },
          {
            key: "createdAt",
            title: "Created At",
            dataIndex: "createdAt",
            width: 160,
            render: (_, record) => {
              if (!record.createdAt) return "-";
              const date = new Date(record.createdAt);
              return new Intl.DateTimeFormat("en-GB", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }).format(date);
            },
          },
          // Only show "Created By" column for admin users
          ...(isAdmin
            ? [
                {
                  key: "createdBy",
                  title: "Created By",
                  dataIndex: "createdBy",
                  width: 140,
                  render: (_: unknown, record: SurveyListItem) => {
                    if (!record.createdBy) {
                      return <span className="text-gray-400">-</span>;
                    }
                    const fullName =
                      record.createdBy.firstName || record.createdBy.lastName
                        ? `${record.createdBy.firstName || ""} ${
                            record.createdBy.lastName || ""
                          }`.trim()
                        : null;
                    return (
                      <Tooltip title={fullName}>
                        <Tag color="geekblue">{record.createdBy.username}</Tag>
                      </Tooltip>
                    );
                  },
                },
              ]
            : []),
          {
            key: "actions",
            title: "Actions",
            width: showSuperAdminFeatures ? 300 : 220,
            fixed: "right" as const,
            render: (_, record) => (
              <Space>
                <Tooltip title="View / Edit">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`./edit/${record.id}`)}
                  />
                </Tooltip>
                <Tooltip title="View Responses">
                  <Button
                    type="text"
                    icon={<UnorderedListOutlined />}
                    onClick={() => navigate(`./responses/${record.id}`)}
                  />
                </Tooltip>
                <Tooltip title="Statistics">
                  <Button
                    type="text"
                    icon={<BarChartOutlined />}
                    onClick={() => navigate(`./stats/${record.id}`)}
                  />
                </Tooltip>
                <Tooltip title="Preview Survey">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      const url = `/s/${record.publicId}`;
                      window.open(url, "_blank");
                    }}
                  />
                </Tooltip>
                {showSuperAdminFeatures && (
                  <Tooltip title="Export Survey">
                    <Button
                      type="text"
                      icon={<DownloadOutlined />}
                      onClick={() => handleExport(record.id, record.publicId)}
                    />
                  </Tooltip>
                )}
                {showSuperAdminFeatures && (
                  <Tooltip title="Change Ownership">
                    <Button
                      type="text"
                      icon={<LockOutlined />}
                      onClick={() => handleOpenChangeOwnerModal(record.id)}
                    />
                  </Tooltip>
                )}
                <Popconfirm
                  title="Delete Survey"
                  description="Are you sure you want to delete this survey? All responses will be lost."
                  onConfirm={() => handleDelete(record.id)}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                >
                  <Tooltip title="Delete">
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      {/* Change Ownership Modal */}
      <Modal
        title="Change Survey Ownership"
        open={changeOwnerModalOpen}
        onCancel={handleCloseChangeOwnerModal}
        footer={[
          <Button key="cancel" onClick={handleCloseChangeOwnerModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={changeOwnerLoading}
            disabled={!selectedUserId || usersLoading}
            onClick={handleChangeOwner}
          >
            Update
          </Button>,
        ]}
      >
        <div className="py-4">
          <p className="mb-3 text-gray-600">
            Select a new owner for this survey:
          </p>
          <Select
            style={{ width: "100%" }}
            placeholder="Select a user"
            loading={usersLoading}
            value={selectedUserId}
            onChange={(value) => setSelectedUserId(value)}
            showSearch
            optionFilterProp="label"
            options={users.map((u) => ({
              value: u.id,
              label: u.firstName || u.lastName
                ? `${u.username} (${u.firstName || ""} ${u.lastName || ""}`.trim() + ")"
                : u.username,
            }))}
          />
        </div>
      </Modal>
    </AntPageContainer>
  );
};
